<script setup>
import { computed, nextTick } from 'vue';
import { useStore } from 'vuex';

import MessageMeta from '../MessageMeta.vue';
import CaptainGenerationDetails from '../CaptainGenerationDetails.vue';

import { emitter } from 'shared/helpers/mitt';
import { useMessageContext } from '../provider.js';
import { useI18n } from 'vue-i18n';

import MessageFormatter from 'shared/helpers/MessageFormatter.js';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { MESSAGE_VARIANTS, ORIENTATION, SENDER_TYPES } from '../constants';

const props = defineProps({
  hideMeta: { type: Boolean, default: false },
});

const {
  variant,
  orientation,
  inReplyTo,
  conversationId,
  shouldGroupWithNext,
  id,
  sender,
  senderType,
} = useMessageContext();
const { t } = useI18n();
const store = useStore();

const isCaptainMessage = computed(
  () =>
    (sender.value?.type ?? senderType.value) === SENDER_TYPES.CAPTAIN_ASSISTANT
);

const metaColorClass = computed(() =>
  variant.value === MESSAGE_VARIANTS.PRIVATE
    ? 'text-n-amber-12/50'
    : 'text-n-slate-11'
);

const emailMetaClass = computed(() =>
  variant.value === MESSAGE_VARIANTS.EMAIL ? 'px-3 pb-3' : ''
);

const varaintBaseMap = {
  [MESSAGE_VARIANTS.AGENT]: 'bg-n-solid-blue text-n-slate-12',
  [MESSAGE_VARIANTS.PRIVATE]:
    'bg-n-solid-amber text-n-amber-12 [&_.prosemirror-mention-node]:font-semibold',
  [MESSAGE_VARIANTS.USER]: 'bg-n-slate-4 text-n-slate-12',
  [MESSAGE_VARIANTS.ACTIVITY]: 'bg-n-alpha-1 text-n-slate-11 text-sm',
  [MESSAGE_VARIANTS.BOT]: 'bg-n-solid-iris text-n-slate-12',
  [MESSAGE_VARIANTS.TEMPLATE]: 'bg-n-solid-iris text-n-slate-12',
  [MESSAGE_VARIANTS.ERROR]: 'bg-n-ruby-4 text-n-ruby-12',
  [MESSAGE_VARIANTS.EMAIL]: 'w-full',
  [MESSAGE_VARIANTS.UNSUPPORTED]:
    'bg-n-solid-amber/70 border border-dashed border-n-amber-12 text-n-amber-12',
};

const orientationMap = {
  [ORIENTATION.LEFT]:
    'left-bubble rounded-xl ltr:rounded-bl-sm rtl:rounded-br-sm',
  [ORIENTATION.RIGHT]:
    'right-bubble rounded-xl ltr:rounded-br-sm rtl:rounded-bl-sm',
  [ORIENTATION.CENTER]: 'rounded-md',
};

const flexOrientationClass = computed(() => {
  const map = {
    [ORIENTATION.LEFT]: 'justify-start',
    [ORIENTATION.RIGHT]: 'justify-end',
    [ORIENTATION.CENTER]: 'justify-center',
  };

  return map[orientation.value];
});

const messageClass = computed(() => {
  const classToApply = [varaintBaseMap[variant.value]];

  if (variant.value !== MESSAGE_VARIANTS.ACTIVITY) {
    classToApply.push(orientationMap[orientation.value]);
  } else {
    classToApply.push('rounded-lg');
  }

  return classToApply;
});

const highlightReplyTarget = messageId => {
  const messageElement = document.getElementById(`message${messageId}`);
  if (!messageElement) return;

  messageElement.classList.add('reply-target-highlight');
  window.setTimeout(
    () => messageElement.classList.remove('reply-target-highlight'),
    2200
  );
};

const scrollToMessage = async () => {
  const messageId = inReplyTo.value?.id;
  if (!messageId) return;

  // Reply targets can be older than the loaded timeline. Insert only the
  // referenced message before attempting the jump.
  if (!document.getElementById(`message${messageId}`)) {
    await store.dispatch('fetchPreviousMessages', {
      conversationId: conversationId.value,
      before: messageId + 1,
      after: messageId,
    });
    await nextTick();
  }

  emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE, {
    messageId,
  });
  window.setTimeout(() => highlightReplyTarget(messageId), 250);
};

const shouldShowMeta = computed(
  () =>
    !props.hideMeta &&
    !shouldGroupWithNext.value &&
    variant.value !== MESSAGE_VARIANTS.ACTIVITY
);

const replyToPreview = computed(() => {
  if (!inReplyTo.value) return '';

  const { content, attachments } = inReplyTo.value;

  if (content) return new MessageFormatter(content).formattedMessage;
  if (attachments?.length) {
    const firstAttachment = attachments[0];
    const fileType = firstAttachment.fileType ?? firstAttachment.file_type;

    return t(`CHAT_LIST.ATTACHMENTS.${fileType}.CONTENT`);
  }

  return t('CONVERSATION.REPLY_MESSAGE_NOT_FOUND');
});

const replyAttachment = computed(() => inReplyTo.value?.attachments?.[0]);

const replyAttachmentType = computed(
  () => replyAttachment.value?.fileType ?? replyAttachment.value?.file_type
);

const replyAttachmentUrl = computed(
  () => replyAttachment.value?.thumbUrl ?? replyAttachment.value?.thumb_url
);

const isImageReply = computed(
  () => replyAttachmentType.value === 'image' && replyAttachmentUrl.value
);

const replyAttachmentLabel = computed(() => {
  if (!replyAttachment.value) return '';

  const fileName =
    replyAttachment.value.fileName ?? replyAttachment.value.file_name;
  if (fileName) return fileName;

  const extension = replyAttachment.value.extension;
  const type = replyAttachmentType.value;
  const labels = {
    image: t('CHAT_LIST.ATTACHMENTS.image.CONTENT'),
    audio: t('CHAT_LIST.ATTACHMENTS.audio.CONTENT'),
    video: t('CHAT_LIST.ATTACHMENTS.video.CONTENT'),
    file: t('CHAT_LIST.ATTACHMENTS.file.CONTENT'),
  };
  const label = labels[type] ?? t('CONVERSATION.REPLY_MESSAGE_NOT_FOUND');

  return extension ? `${label} (${extension.toUpperCase()})` : label;
});
</script>

<template>
  <div
    class="text-sm min-w-0"
    :class="[
      messageClass,
      {
        'max-w-lg': variant !== MESSAGE_VARIANTS.EMAIL,
      },
    ]"
  >
    <div
      v-if="inReplyTo"
      class="p-2 -mx-1 mb-2 rounded-lg cursor-pointer bg-n-alpha-black1"
      @click="scrollToMessage"
    >
      <div class="flex gap-2 min-w-0">
        <img
          v-if="isImageReply"
          :src="replyAttachmentUrl"
          alt="Imagem respondida"
          class="w-12 h-12 shrink-0 object-cover rounded-md"
        />
        <div class="min-w-0">
          <div
            v-dompurify-html="replyToPreview"
            class="prose prose-bubble line-clamp-2"
          />
          <div
            v-if="replyAttachment"
            class="mt-1 text-xs text-n-slate-11 line-clamp-1"
          >
            {{ replyAttachmentLabel }}
          </div>
        </div>
      </div>
    </div>
    <slot />
    <template v-if="shouldShowMeta">
      <CaptainGenerationDetails
        v-if="isCaptainMessage"
        :message-id="id"
        class="mt-2"
      >
        <template #meta>
          <MessageMeta :class="[emailMetaClass, metaColorClass]" />
        </template>
      </CaptainGenerationDetails>
      <MessageMeta
        v-else
        :class="[flexOrientationClass, emailMetaClass, metaColorClass]"
        class="mt-2"
      />
    </template>
  </div>
</template>

<style>
@keyframes reply-target-highlight {
  0%,
  100% {
    box-shadow: none;
  }
  30%,
  70% {
    box-shadow: 0 0 0 3px rgb(37 99 235 / 55%);
  }
}

.reply-target-highlight {
  animation: reply-target-highlight 2.2s ease-in-out;
}
</style>
